import { z } from 'zod';
import { ReferralPatientSchema } from '../ReferralPatientSchema';
import { ReferralVitalityCaseManagerSchema } from './ReferralVitalityCaseManagerSchema';
import { IObject } from '../../IObject';
import { ReferralBaseSchema } from '../ReferralBaseSchema';
import { ReferralCoverageSchema } from '../ReferralCoverageSchema';
import { ReferralIdSchema } from '../ReferralDocumentsSchema';
import { ReferralDiagnosisSchema } from '../ReferralDiagnosisSchema';
import { OrganizationSchema } from '../../OrganizationSchema';
import { ReferralNomineeSchema } from '../ReferralNomineeSchema';
import {
  ReferralConsentRequestSchema,
  ReferralConsentSchema,
  requiresPatientPhoneForConsent,
  requiresPatientConsentEvidence,
} from '../ReferralConsentSchema';
import { ReferralNotesSchema } from '../ReferralNotesSchema';
import { allJoinReasonIds } from '../../../data/joinReasons';
import { joinReasonIdSchema } from '../ReferralJoinReasonSchema';
import { requiredString, requiredTrimmedString } from '../../../validation';
import { withLegacyPlanIdentifiers } from '../hoistPlanIdentifiersToPatient';

const vitalityBaseFields = {
  // The referral ID a body may state. Optional for any caller: the endpoint
  // mints one when it is omitted. The form does state one, minted once per
  // mounted form, so a retry after a lost response hits the replay guard
  // instead of creating a second referral (PPL-3950)
  id: ReferralIdSchema.optional(),
  // Both plan identifiers are mandatory on the Vitality form, in their
  // standard home on the patient; "entity number" is Vitality's label for
  // the policy number
  patient: ReferralPatientSchema.extend({
    policyNumber: requiredTrimmedString('Entity number is required'),
    claimNumber: requiredTrimmedString('Claim number is required'),
  }),
  coverage: ReferralCoverageSchema,
  diagnosis: ReferralDiagnosisSchema,
  // At the referral root, like every other party on the referral — not inside
  // a nested `referral` object (that CLUK/default-era wrapper holds booking
  // preferences and is legacy)
  nominee: ReferralNomineeSchema,
  joinReasonId: joinReasonIdSchema(allJoinReasonIds).optional(),
  // The case manager, like Aviva's and L&G's. A Vitality referral has no
  // referrer: the public form does not know who submits it (PPL-4211)
  caseManager: ReferralVitalityCaseManagerSchema,
  // Optional free text the case manager typed on the referral form. Internal
  // only: it is never shown to the member (see ReferralNotesSchema)
  notes: ReferralNotesSchema,
  // How we know the patient consented to being referred. The REQUEST form:
  // a body states the EVIDENCE only — the status and what we record when we
  // ask are ours to write (see ReferralConsentSchema; the stored schema
  // below carries the full shape)
  consent: ReferralConsentRequestSchema,
};

// Schema for validating incoming POST /referrals request. The form ASKS the
// case manager to confirm the patient's consent, so the answer is required. A
// Vitality referral carries no documents at all: Vitality asked for the
// uploads to go before the form went live (PPL-3950), so a body that still
// sends some has them dropped and nothing stored has any.
export const ReferralCreateVitalitySchema = withLegacyPlanIdentifiers(
  z
    .object({
      ...vitalityBaseFields,
      // The insurance product the member is covered under: the id of one of
      // the Vitality sub-sub-organizations. Which ids are allowed is a
      // question for the organization tree, so the endpoint checks it
      // (see getVitalityReferralOrganizationIds)
      organizationId: requiredString(
        'Insurance product (organization) is required',
      ),
    })
    .check(requiresPatientPhoneForConsent)
    .check(requiresPatientConsentEvidence),
);

// Schema for how it's stored in Firestore (extends base with status, type, etc.)
export const ReferralVitalitySchema = ReferralBaseSchema.extend({
  ...vitalityBaseFields,
  // as stored: the evidence and status the body stated, plus whatever we
  // recorded when we asked the patient ourselves
  consent: ReferralConsentSchema,
  joinReasonId: joinReasonIdSchema(allJoinReasonIds),
  organization: OrganizationSchema,
});

export type ReferralCreateVitality = z.infer<
  typeof ReferralCreateVitalitySchema
>;
export type ReferralVitalityData = z.infer<typeof ReferralVitalitySchema>;
export type ReferralVitality = IObject.HasIdTimestamped<ReferralVitalityData>;
